import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Notification } from '../store';
import { AlertMessages } from '../../assets/data/alert-messages';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  toastrRef: HTMLElement | null = null;
  private toastObserver: MutationObserver | null = null;

  constructor(private readonly toasterService: ToastrService) {}

  showErrorAlert(notification: Notification, options): void {
    if (
      !(notification.title === 'HttpErrorResponse' && notification.message.includes(AlertMessages.DuplicateAppMessage))
    )
      notification.title === 'HttpErrorResponse' &&
        (notification = { ...notification, message: AlertMessages[notification.title] });
    const errorTitle = notification.message?.includes(AlertMessages.DuplicateAppMessage)
      ? AlertMessages.DuplicateAppTitle
      : notification.title;

    this.toasterService.error(notification.message, errorTitle, options);
    this.deferToastAccessibilityNormalization();
  }

  showWarningAlert(notification: Notification, options): void {
    this.toasterService.warning(notification.message, notification.title, options);
    this.deferToastAccessibilityNormalization();
  }

  showSuccessAlert(notification: Notification, options): void {
    this.toasterService.success(notification.message, notification.title, options);
    this.deferToastAccessibilityNormalization();
  }

  setAttributeForToastrForScreenReaders(): void {
    this.toastrRef = document.getElementById('toast-container') || document.querySelector<HTMLElement>('.toast-container');
    if (!this.toastrRef) {
      return;
    }

    this.toastrRef.setAttribute('role', 'region');
    this.toastrRef.setAttribute('aria-label', 'Notifications');
    this.toastrRef.setAttribute('aria-live', 'off');
    this.toastrRef.setAttribute('aria-atomic', 'false');
    this.toastrRef.setAttribute('aria-relevant', 'additions text');

    this.normalizeToastMarkup();
    requestAnimationFrame(() => this.normalizeToastMarkup());
    setTimeout(() => this.normalizeToastMarkup());
    this.observeToastMarkup();
  }

  private normalizeToastMarkup(): void {
    if (!this.toastrRef) {
      return;
    }

    Array.from(this.toastrRef.querySelectorAll('.ngx-toastr')).forEach(toast => {
      const isAssertive = toast.classList.contains('toast-error') || toast.classList.contains('toast-warning');

      toast.setAttribute('role', isAssertive ? 'alert' : 'status');
      toast.setAttribute('aria-live', isAssertive ? 'assertive' : 'polite');
      toast.setAttribute('aria-atomic', 'true');

      Array.from(toast.querySelectorAll('.toast-title[aria-label]')).forEach(title => {
        title.removeAttribute('aria-label');
      });

      // Preserve heading semantics after removing invalid aria-label from generic div titles.
      Array.from(toast.querySelectorAll('.toast-title')).forEach(title => {
        title.setAttribute('role', 'heading');
        title.setAttribute('aria-level', '2');
      });
    });
  }

  private observeToastMarkup(): void {
    if (!this.toastrRef || typeof MutationObserver === 'undefined') {
      return;
    }

    this.toastObserver?.disconnect();
    this.toastObserver = new MutationObserver(() => {
      this.normalizeToastMarkup();
    });

    this.toastObserver.observe(this.toastrRef, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'class'],
    });
  }

  private deferToastAccessibilityNormalization(): void {
    setTimeout(() => this.setAttributeForToastrForScreenReaders());
  }
}
